import React from "react";

import { useLingui } from "@lingui/react";
import { Trans } from "@lingui/react/macro";

import { Divider, Skeleton, Typography } from "@mui/material";

import AutoAnimateContainer from "@readai/common/components/AutoAnimateContainer";
import Avatar from "@readai/common/components/Avatar";
import Link from "@readai/common/components/Link";
import useAuth from "@readai/common/hooks/useAuth";
import type { User } from "@readai/common/types";
import { getTimezoneName } from "@readai/common/utils";
import { formatStartAndEndTime } from "@readai/common/utils/datetimeUtils";
import ArrowIcon from "@readai/icons/uui/ArrowRight";
import ChevronUpIcon from "@readai/icons/uui/ChevronUp";
import IconButton from "@readai/pulse/components/IconButton";
import Stack, { Row } from "@readai/pulse/components/Stack";

import ReadSmartSchedulerLogo from "@app/booking/components/ReadSmartSchedulerLogo";
import ROUTES from "@app/booking/routes";
import useSchedulingStore from "@app/booking/stores/scheduling";

import {
  LoggedInUserContentContainer,
  OuterUserPanel,
  UserPanel,
} from "./styles";

const NoUserContent = () => (
  <Stack>
    <Typography variant="bodySecondaryReg" color="text.weak">
      <Trans>
        Already use Read AI?{" "}
        <Link href={ROUTES.MAIN_APP.SIGN_IN} underline>
          Log in
        </Link>{" "}
        to find the time that works best for both of you, automatically.
      </Trans>
    </Typography>
  </Stack>
);

const LoggedInUserContent = ({ user }: { user: User }) => (
  <Link href={ROUTES.MAIN_APP.LANDING_PAGE}>
    <LoggedInUserContentContainer spacing={0.5}>
      <Row variant="apart" spacing={1}>
        <Typography variant="bodySecondaryReg" color="text.default">
          <Trans>Logged in as {user.fullName}</Trans>
        </Typography>
        <ArrowIcon sx={{ fontSize: "18px" }} />
      </Row>
      <Typography variant="labelReg">{user.email}</Typography>
    </LoggedInUserContentContainer>
  </Link>
);

const MeetingDetails = ({ expanded }: { expanded: boolean }) => {
  const { i18n } = useLingui();
  const { selectedDate, selectedStartTime, duration, timezone } =
    useSchedulingStore((state) => ({
      selectedDate: state.selectedDate,
      selectedStartTime: state.selectedTime,
      duration: state.constraints?.allowedDuration ?? null,
      timezone: state.selectedTimezone,
    }));

  const endTime = selectedStartTime?.plus({
    minutes: duration ?? 0,
  });

  const showDateAndTime = selectedStartTime != null;
  const showDuration = !showDateAndTime && duration != null;
  const selectedDay = selectedStartTime ?? selectedDate;

  return (
    <>
      <Stack color="text.weak">
        <AutoAnimateContainer show={duration != null && expanded}>
          <Stack paddingTop={2} paddingBottom={2}>
            <Divider />
          </Stack>
        </AutoAnimateContainer>
        <AutoAnimateContainer show={selectedDay != null}>
          <Typography variant="bodyPrimaryMed">
            {selectedDay?.toLocaleString(
              {
                weekday: "long",
                month: "long",
                day: "numeric",
                year: "numeric",
              },
              { locale: i18n.locale },
            )}
          </Typography>
        </AutoAnimateContainer>
        <AutoAnimateContainer show={showDuration}>
          <Typography
            variant="bodyPrimaryMed"
            marginTop={selectedDay == null ? 0 : 0.5}
          >
            <Trans>{duration}-minute meeting</Trans>
          </Typography>
        </AutoAnimateContainer>
        <AutoAnimateContainer show={showDateAndTime}>
          <Stack spacing={0.5} marginTop={0.5}>
            <Typography variant="bodyPrimaryMed">
              {formatStartAndEndTime(selectedStartTime!, endTime, {
                locale: i18n.locale,
              })}
            </Typography>
            {timezone && (
              <Typography variant="labelReg">
                {getTimezoneName(timezone)}
              </Typography>
            )}
          </Stack>
        </AutoAnimateContainer>
      </Stack>
    </>
  );
};

const UserPanelContent = ({
  expanded,
  expandable,
  isLoading,
  onToggleExpand,
  ownerName,
}: {
  expanded: boolean;
  expandable: boolean;
  isLoading: boolean;
  onToggleExpand: () => void;
  ownerName?: string;
}) => {
  const { user } = useAuth();

  return (
    <OuterUserPanel>
      <UserPanel>
        <AutoAnimateContainer show={expanded}>
          <Row paddingBottom={4}>
            <ReadSmartSchedulerLogo />
          </Row>
          <Typography
            variant="bodyPrimaryMed"
            color="text.weak"
            paddingBottom={0.5}
          >
            <Trans>Scheduling a meeting with</Trans>
          </Typography>
        </AutoAnimateContainer>
        <Stack>
          <Row
            variant="apart"
            spacing={0.5}
            sx={{ color: "text.weak", minHeight: "35px" }}
          >
            <Stack spacing={1}>
              <Row variant="left" spacing={1.5}>
                <Avatar name={ownerName ?? ""} />
                {isLoading && !ownerName ? (
                  <Skeleton width="200px" />
                ) : (
                  <Typography
                    variant="h2"
                    color="text.default"
                    fontWeight={500}
                  >
                    {ownerName ?? "-"}
                  </Typography>
                )}
              </Row>
            </Stack>
            {expandable && (
              <IconButton onClick={onToggleExpand} size="small">
                <ChevronUpIcon
                  sx={{ transform: expanded ? undefined : "rotate(180deg)" }}
                />
              </IconButton>
            )}
          </Row>
          <MeetingDetails expanded={expanded} />
        </Stack>
        <AutoAnimateContainer show={expanded}>
          <Stack paddingTop={4}>
            {user ? <LoggedInUserContent user={user} /> : <NoUserContent />}
          </Stack>
        </AutoAnimateContainer>
      </UserPanel>
    </OuterUserPanel>
  );
};

export default UserPanelContent;
